import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiMessageCircle, FiX, FiClock, FiMenu, FiPlus } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { settingsService } from '../../services/settingsService';

// Keyframes para animaciones
const mainButtonPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(253, 191, 55, 0.3), 0 0 0 0 rgba(253, 191, 55, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 6px 25px rgba(253, 191, 55, 0.4), 0 0 0 8px rgba(253, 191, 55, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(253, 191, 55, 0.3), 0 0 0 0 rgba(253, 191, 55, 0.4);
  }
`;

const glowEffect = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 5px rgba(253, 191, 55, 0.3));
  }
  50% {
    filter: drop-shadow(0 0 15px rgba(253, 191, 55, 0.6));
  }
`;

const badgePulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 8px rgba(255, 59, 48, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
  }
`;

const badgeBounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -8px, 0);
  }
  70% {
    transform: translate3d(0, -4px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
`;

// Convertir iconos a componentes React válidos
const MessageCircle = FiMessageCircle as React.ComponentType;
const X = FiX as React.ComponentType;
const Clock = FiClock as React.ComponentType;
const Menu = FiMenu as React.ComponentType;
const Plus = FiPlus as React.ComponentType;
const WhatsappIcon = FaWhatsapp as React.ComponentType;

// Interfaces
interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
  isTyping?: boolean;
}

interface PredefinedQuestion {
  id: string;
  question: string;
  answer: string;
}

// Preguntas y respuestas predeterminadas
const predefinedQuestions: PredefinedQuestion[] = [
  {
    id: '1',
    question: '¿Cuál es el horario de atención?',
    answer: 'Nuestro horario de atención en Vargas Homecenter es de lunes a sabado de 8:00 AM a 8:00 PM. ¡Estamos aquí para ayudarte!'
  },
  {
    id: '2',
    question: '¿Dónde están ubicados?',
    answer: "Puedes encontrarnos en Av. Centenario #220. ¡Te esperamos con gusto!"
  },
  {
    id: '3',
    question: '¿Hacen entregas a domicilio?',
    answer: 'Sí, realizamos entregas a domicilio. Los tiempos y costos de envío varían según tu ubicación. ¡Consulta con nuestro equipo para más detalles!'
  },
  {
    id: '4',
    question: '¿Cómo puedo hacer una consulta de productos?',
    answer: 'Puedes agregar productos a tu carrito de compras y luego enviar tu lista. Nuestro equipo te contactará con la información detallada y precios.'
  }
];

// Styled Components
const SpeedDialContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isChatOpen'].includes(prop)
})<{ isChatOpen?: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: ${props => props.isChatOpen ? 9990 : 9998};
  max-width: none !important; /* Override global max-width rule */
  
  @media (max-width: 768px) {
    bottom: 15px;
    right: 15px;
  }
  
  @media (max-width: 415px) {
    bottom: 10px;
    right: 10px;
    max-width: none !important;
  }
`;

const MainButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FDBF37 0%, #f0a500 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 20px rgba(253, 191, 55, 0.3);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
  max-width: none !important; /* Override global max-width rule */
  animation: ${mainButtonPulse} 3s ease-in-out infinite;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover {
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(1.15)' : 'scale(1.15)'};
    box-shadow: 0 8px 30px rgba(253, 191, 55, 0.5), 0 0 0 3px rgba(253, 191, 55, 0.3);
    background: linear-gradient(135deg, #f0a500 0%, #FDBF37 100%);
    animation: ${glowEffect} 1.5s ease-in-out infinite;
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: ${props => props.isOpen ? 'rotate(45deg) scale(0.95)' : 'scale(0.95)'};
    transition: all 0.1s ease;
  }
  
  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    font-size: 1.3rem;
  }
  
  @media (max-width: 415px) {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
    max-width: none !important;
  }
`;

const MiniButtonsContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  position: absolute;
  bottom: 0;
  right: 0;
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  max-width: none !important; /* Override global max-width rule */
  z-index:-1;
`;

const MiniButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['isOpen', 'delay', 'bgColor'].includes(prop)
})<{ isOpen: boolean; delay: number; bgColor: string }>`
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.bgColor};
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  opacity: ${props => props.isOpen ? 1 : 0};
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0)'};
  transition-delay: ${props => props.isOpen ? `${props.delay}ms` : '0ms'};
  max-width: none !important; /* Override global max-width rule */
  
  &:hover {
    transform: ${props => props.isOpen ? 'scale(1.1)' : 'scale(0)'};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 415px) {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    max-width: none !important;
  }
`;

const FaqButton = styled(MiniButton)`
  bottom: 75px;
  right: 6px;
  
  @media (max-width: 415px) {
    bottom: 65px;
    right: 5px;
  }
`;

const WhatsappButton = styled(MiniButton)`
  bottom: 130px;
  right: 6px;
  position: relative;
  
  @media (max-width: 415px) {
    bottom: 115px;
    right: 5px;
  }
`;

const NotificationBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #FF3B30 0%, #FF6B6B 100%);
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(255, 59, 48, 0.4);
  animation: ${badgePulse} 2s infinite;
  z-index: 9997;
  pointer-events: none;
  
  &:hover {
    animation: ${badgeBounce} 1s ease-in-out;
  }
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    font-size: 0.7rem;
    top: -6px;
    right: -6px;
  }
  
  @media (max-width: 415px) {
    width: 20px;
    height: 20px;
    font-size: 0.65rem;
    top: -5px;
    right: -5px;
  }
`;

const ChatModal = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop)
})<{ isOpen: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 350px;
  height: 500px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 9999;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: calc(100vw - 80px);
    max-width: 320px;
    height: 450px;
    bottom: 80px;
    right: 15px;
    left: auto;
  }
  
  @media (max-width: 415px) {
    width: calc(100vw - 40px);
    max-width: 300px;
    height: 400px;
    bottom: 70px;
  }
`;

const ChatHeader = styled.div`
  background: #242A5B;
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  position: relative;
  z-index: 99999;
  margin: -0.25rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.05);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(0.95);
  }
  
  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
`;

const ChatBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  
  /* Estilos de scrollbar personalizados */
  scrollbar-width: thin;
  scrollbar-color: #FDBF37 #f1f1f1;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #FDBF37;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #e8891a;
  }
`;

const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`;

const QuestionsSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
`;

const WelcomeMessage = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid #FDBF37;
  margin-bottom: 1rem;
`;

const WelcomeText = styled.p`
  margin: 0;
  color: #333;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const QuestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const QuestionButton = styled.button<{ index?: number }>`
  background: white;
  border: 2px solid #e9ecef;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: #333;
  opacity: 0;
  transform: translateY(20px);
  animation: ${props => `slideInQuestion 0.6s ease-out ${(props.index || 0) * 0.15}s forwards`};

  &:hover {
    border-color: #FDBF37;
    background: #fff8f0;
  }

  @keyframes slideInQuestion {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ChatMessages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TypingIndicator = styled.div`
  background: #f8f9fa;
  color: #333;
  padding: 0.75rem;
  border-radius: 10px;
  max-width: 80%;
  align-self: flex-start;
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 2rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #FDBF37;
    animation: typing 1.4s infinite ease-in-out;
    margin-right: 4px;
  }
  
  &::after {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #FDBF37;
    animation: typing 1.4s infinite ease-in-out 0.2s;
    margin-right: 4px;
  }
  
  span {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #FDBF37;
    animation: typing 1.4s infinite ease-in-out 0.4s;
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

const TypingText = styled.div`
  background: #f8f9fa;
  color: #333;
  padding: 0.75rem;
  border-radius: 10px;
  max-width: 80%;
  align-self: flex-start;
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
  min-height: 2.5rem;
  display: flex;
  align-items: flex-start;
  position: relative;

  &::after {
    content: '|';
    animation: blink 1s infinite;
    color: #FDBF37;
    margin-left: 2px;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const MessageBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isBot',
})<{ isBot: boolean }>`
  background: ${props => props.isBot ? '#f8f9fa' : '#FDBF37'};
  color: ${props => props.isBot ? '#333' : 'white'};
  padding: 0.75rem;
  border-radius: 10px;
  max-width: 80%;
  align-self: ${props => props.isBot ? 'flex-start' : 'flex-end'};
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
`;

const FloatingSpeedDial: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showQuestions, setShowQuestions] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [hasStartedConversation, setHasStartedConversation] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('+51962623633');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);

  // Cargar número de WhatsApp al montar el componente
  useEffect(() => {
    const loadWhatsappNumber = async () => {
      try {
        const settings = await settingsService.getSettings();
        if (settings.whatsapp_number) {
          setWhatsappNumber(settings.whatsapp_number);
        }
      } catch (error) {
        console.error('Error loading WhatsApp number:', error);
      }
    };
    
    loadWhatsappNumber();
  }, []);

  // Función para hacer scroll hacia abajo
  const scrollToBottom = () => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  };

  // useEffect para scroll automático cuando cambian los mensajes
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, typingText]);

  const toggleSpeedDial = () => {
    setIsOpen(!isOpen);
    // Cerrar chat si está abierto
    if (isChatOpen) {
      setIsChatOpen(false);
    }
  };

  const handleFaqClick = () => {
    setIsOpen(false);
    setIsChatOpen(true);
    setTimeout(scrollToBottom, 100);
  };

  const handleWhatsappClick = () => {
    setIsOpen(false);
    const message = encodeURIComponent('¡Hola! Me gustaría obtener más información sobre sus productos y servicios.');
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setTimeout(scrollToBottom, 100);
    }
  };

  const typewriterEffect = (text: string, callback: () => void) => {
    setTypingText('');
    let index = 0;
    let currentText = '';
    
    const typeInterval = setInterval(() => {
      if (index < text.length) {
        currentText += text.charAt(index);
        setTypingText(currentText);
        index++;
      } else {
        clearInterval(typeInterval);
        callback();
      }
    }, 30);
    
    return () => clearInterval(typeInterval);
  };

  const handleQuestionClick = (question: PredefinedQuestion) => {
    if (!hasStartedConversation) {
      setHasStartedConversation(true);
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      text: question.question,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setShowQuestions(false);
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      
      const botMessage: ChatMessage = {
        id: `bot-${Date.now()}`,
        text: question.answer,
        isBot: true,
        timestamp: new Date(),
        isTyping: true
      };
      
      setMessages(prev => [...prev, botMessage]);
      
      typewriterEffect(question.answer, () => {
        setMessages(prev => 
          prev.map(msg => 
            msg.id === botMessage.id 
              ? { ...msg, isTyping: false }
              : msg
          )
        );
        setTypingText('');
        
        setTimeout(() => {
          const followUpMessage: ChatMessage = {
            id: `followup-${Date.now()}`,
            text: '¿Puedo ayudarte con algo más? 😊',
            isBot: true,
            timestamp: new Date(),
            isTyping: true
          };
          
          setMessages(prev => [...prev, followUpMessage]);
          
          const followUpText = '¿Puedo ayudarte con algo más? 😊';
          let currentIndex = 0;
          
          const typeFollowUp = () => {
            if (currentIndex < followUpText.length) {
              setTypingText(followUpText.substring(0, currentIndex + 1));
              currentIndex++;
              setTimeout(typeFollowUp, 50);
            } else {
              setTimeout(() => {
                setMessages(prev => 
                  prev.map(msg => 
                    msg.id === followUpMessage.id 
                      ? { ...msg, isTyping: false }
                      : msg
                  )
                );
                setTypingText('');
                setShowQuestions(true);
              }, 500);
            }
          };
          
          typeFollowUp();
        }, 1500);
      });
    }, 2000);
  };

  return (
    <>
      <SpeedDialContainer isChatOpen={isChatOpen}>
        <MainButton onClick={toggleSpeedDial} isOpen={isOpen}>
          {isOpen ? <Plus /> : <Menu />}
        </MainButton>
        
        <MiniButtonsContainer isOpen={isOpen}>
          <FaqButton
            isOpen={isOpen}
            delay={100}
            bgColor="#242A5B"
            onClick={handleFaqClick}
            title="Preguntas Frecuentes"
          >
            <MessageCircle />
          </FaqButton>
          
          <WhatsappButton
            isOpen={isOpen}
            delay={200}
            bgColor="#25D366"
            onClick={handleWhatsappClick}
            title="Contactar por WhatsApp"
          >
            <WhatsappIcon />
            <NotificationBadge>
              1
            </NotificationBadge>
          </WhatsappButton>
        </MiniButtonsContainer>
      </SpeedDialContainer>
      
      <ChatModal isOpen={isChatOpen}>
        <ChatHeader>
          <ChatTitle>Asistente Virtual Vargas</ChatTitle>
          <CloseButton onClick={toggleChat}>
            <X />
          </CloseButton>
        </ChatHeader>
        
        <ChatBody ref={chatContentRef}>
          <ChatContent>
            {!hasStartedConversation && messages.length === 0 && !isTyping && !typingText ? (
              <>
                <WelcomeMessage>
                  <WelcomeText>
                    ¡Hola! 👋 Soy el asistente virtual de Vargas Homecenter. 
                    Estoy aquí para ayudarte con tus consultas. 
                    ¿En qué puedo asistirte hoy?
                  </WelcomeText>
                </WelcomeMessage>
                
                <QuestionsContainer>
                  {predefinedQuestions.map((question, index) => (
                    <QuestionButton
                      key={question.id}
                      index={index}
                      onClick={() => handleQuestionClick(question)}
                    >
                      {question.question}
                    </QuestionButton>
                  ))}
                </QuestionsContainer>
              </>
            ) : (
              <>
                <ChatMessages>
                  {messages.filter(message => !message.isTyping).map((message) => (
                    <MessageBubble key={message.id} isBot={message.isBot}>
                      {message.text}
                    </MessageBubble>
                  ))}
                  
                  {isTyping && !typingText && (
                    <TypingIndicator>
                      <span></span>
                    </TypingIndicator>
                  )}
                  
                  {typingText && (
                    <TypingText>
                      {typingText}
                    </TypingText>
                  )}
                  
                  <div ref={messagesEndRef} />
                </ChatMessages>
                
                {showQuestions && (
                  <QuestionsSection>
                    <QuestionsContainer>
                      {predefinedQuestions.map((question, index) => (
                        <QuestionButton
                          key={question.id}
                          index={index}
                          onClick={() => handleQuestionClick(question)}
                        >
                          {question.question}
                        </QuestionButton>
                      ))}
                    </QuestionsContainer>
                  </QuestionsSection>
                )}
              </>
            )}
          </ChatContent>
        </ChatBody>
      </ChatModal>
    </>
  );
};

export default FloatingSpeedDial;